import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import PrintInventoryStockLabelsButton from '#/modules/imsMgmt/inventoryStock/components/PrintInventoryStockLabelsButton';
import { formatInventoryDisplayQty } from '#/modules/imsMgmt/inventoryStock/helper/displayInventoryQty';
import {
  formatWarehouseLocation,
} from '#/modules/imsMgmt/inventoryStock/helper/inventoryStockFilterOptions';
import usePrintInventoryStockLabels from '#/modules/imsMgmt/inventoryStock/hooks/usePrintInventoryStockLabels';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { toast } from '#/utils/toast';

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function resolveBaseQty(stock) {
  const qty = stock?.qty?.baseQty;
  const n = Number(qty);
  return Number.isFinite(n) ? n : 0;
}

function collectSplitIds(splits) {
  return (splits ?? []).map((item) => item.id).filter(Boolean);
}

const SUCCESS_LINE_GRID_COLS = 'grid-cols-[7rem_minmax(0,1fr)_5.5rem]';

const SUCCESS_LINE_HEADER_CLASS = cn(
  'grid w-full items-center gap-2 border-b border-slate-200 bg-slate-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  SUCCESS_LINE_GRID_COLS,
);

const SUCCESS_LINE_ROW_CLASS = cn(
  'grid w-full items-center gap-2 px-3 py-2',
  SUCCESS_LINE_GRID_COLS,
);

/**
 * 庫存拆分 Dialog：對齊 UI_DIALOG。
 * 成功後不 toast、不自動關；內文為列表（header＋divide-y）可全選／單選後列印。
 * 複合列印鈕用 PrintInventoryStockLabelsButton；不另開確認 Dialog。
 * 開啟時（remount／onOpenChange true）重置表單、成功態與勾選。
 */
const InventoryStockSplitDialog = ({
  open,
  onOpenChange,
  stock = null,
  isPending = false,
  onConfirm,
}) => {
  const [qtyDrafts, setQtyDrafts] = useState(['']);
  const [successResult, setSuccessResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const printMutation = usePrintInventoryStockLabels();
  const baseQty = resolveBaseQty(stock);
  const isSuccess = Boolean(successResult);

  const parsedQtys = useMemo(
    () => qtyDrafts.map((draft) => toFiniteNumber(draft)),
    [qtyDrafts],
  );
  const sumQty = useMemo(
    () => parsedQtys.reduce((acc, qty) => (
      Number.isFinite(qty) ? acc + qty : acc
    ), 0),
    [parsedQtys],
  );
  const remainingQty = baseQty - sumQty;
  const isSumInvalid = sumQty >= baseQty;

  function handleQtyChange(index, nextValue) {
    if (isSuccess || isPending) {
      return;
    }
    setQtyDrafts((prev) => prev.map((item, i) => (i === index ? nextValue : item)));
  }

  function handleAddRow() {
    if (isSuccess || isPending) {
      return;
    }
    setQtyDrafts((prev) => [...prev, '']);
  }

  function handleRemoveRow(index) {
    if (isSuccess || isPending) {
      return;
    }
    setQtyDrafts((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    });
  }

  function handleOpenChange(nextOpen) {
    if (nextOpen) {
      setQtyDrafts(['']);
      setSuccessResult(null);
      setSelectedIds([]);
    }
    onOpenChange(nextOpen);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleToggleSelect(id, checked) {
    if (!id || printMutation.isPending) {
      return;
    }
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((itemId) => itemId !== id);
    });
  }

  function handleSelectAll(checked) {
    if (printMutation.isPending) {
      return;
    }
    if (checked === true) {
      setSelectedIds(collectSplitIds(successResult?.splits));
      return;
    }
    setSelectedIds([]);
  }

  function handlePrintSplit(item) {
    if (!item?.id || printMutation.isPending) {
      return;
    }
    printMutation.mutate([item.id]);
  }

  async function handleConfirm() {
    if (!stock || isPending || isSuccess) {
      return;
    }
    if (parsedQtys.length === 0) {
      toast.error('請至少新增一筆拆出數量');
      return;
    }
    if (parsedQtys.some((qty) => !Number.isFinite(qty) || qty <= 0)) {
      toast.error('每筆拆出數量須大於 0');
      return;
    }
    if (isSumInvalid) {
      toast.error('必須小於來源數量（不可整筆拆完）');
      return;
    }
    try {
      const result = await onConfirm?.(parsedQtys);
      if (result?.splits) {
        setSuccessResult(result);
        setSelectedIds(collectSplitIds(result.splits));
      }
    } catch {
      // 錯誤 toast 由列表頁 mutation onError 處理
    }
  }

  const mtr = stock?.mtr;
  const successSplits = Array.isArray(successResult?.splits) ? successResult.splits : [];
  const printableIds = collectSplitIds(successSplits);
  const selectedIdsOrdered = printableIds.filter((id) => selectedIds.includes(id));
  const selectedCount = selectedIdsOrdered.length;
  const allSelected = printableIds.length > 0 && selectedCount === printableIds.length;
  const someSelected = selectedCount > 0 && selectedCount < printableIds.length;
  const selectAllChecked = allSelected ? true : (someSelected ? 'indeterminate' : false);
  const isPrinting = printMutation.isPending;

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="庫存拆分"
      description={
        isSuccess
          ? '拆分已完成；關閉後可再次對其他庫存操作。'
          : '拆出新庫存列；庫存編號由後端產生，不可手動指定。'
      }
      headerVariant="muted"
      contentClassName={dialogContentCompactGapClassName}
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={isSuccess ? undefined : handleConfirm}
      confirmText={isPending ? '拆分中…' : '確認拆分'}
      confirmDisabled={isPending || !stock || isSumInvalid}
      cancelText={isSuccess ? '關閉' : '取消'}
    >
      {!stock ? (
        <p className="text-sm text-muted-foreground">尚未選擇庫存。</p>
      ) : isSuccess ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-base font-semibold text-emerald-700">庫存拆分成功！</p>
            <PrintInventoryStockLabelsButton
              selectedInventoryStockIds={selectedIdsOrdered}
              allInventoryStockIds={printableIds}
              printMutation={printMutation}
              defaultItemId="batch"
            />
          </div>
          <ul
            className={cn(
              'm-0 list-none overflow-hidden p-0',
              'rounded border border-slate-200',
              'divide-y divide-slate-200',
            )}
          >
            <li className={SUCCESS_LINE_HEADER_CLASS}>
              <div className="flex min-w-0 items-center gap-2">
                <Checkbox
                  checked={selectAllChecked}
                  disabled={printableIds.length === 0 || isPrinting}
                  onCheckedChange={handleSelectAll}
                  aria-label="全選拆出庫存"
                />
                <span>項次</span>
              </div>
              <span>庫存編號</span>
              <span className="justify-self-end">操作</span>
            </li>
            {successSplits.length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                沒有新拆出的庫存。
              </li>
            ) : (
              successSplits.map((item, index) => {
                const printingThis = (
                  isPrinting && printMutation.variables?.includes(item.id)
                );
                const checked = Boolean(item.id && selectedIds.includes(item.id));

                return (
                  <li
                    key={item.id || item.stockNo || `split-${index}`}
                    className={SUCCESS_LINE_ROW_CLASS}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Checkbox
                        checked={checked}
                        disabled={!item.id || isPrinting}
                        onCheckedChange={(value) => (
                          handleToggleSelect(item.id, value === true)
                        )}
                        aria-label={`選取 ${item.stockNo || `第${index + 1}筆`}`}
                      />
                      <span className="text-sm font-medium tabular-nums text-gray-700">
                        {`${index + 1}.`}
                      </span>
                    </div>
                    <p
                      className="min-w-0 truncate font-mono text-sm font-medium text-slate-800"
                      title={item.stockNo || undefined}
                    >
                      {item.stockNo || '—'}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      className="justify-self-end"
                      disabled={!item.id || isPrinting}
                      onClick={() => handlePrintSplit(item)}
                    >
                      {printingThis ? '列印中…' : '列印'}
                    </Button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <p className="font-mono text-xs text-gray-500">來源庫存編號</p>
            <p className="font-semibold text-gray-900">{stock?.stockNo || '—'}</p>
            <div className="mt-2">
              <p className="text-xs text-gray-500">物料</p>
              <MaterialNameStack name={mtr?.name} code={mtr?.mtrNo} />
            </div>
            <p className="mt-2 text-xs text-gray-500">倉儲</p>
            <p className="text-gray-900">{formatWarehouseLocation(stock)}</p>
            <p className="mt-2 text-xs text-gray-500">來源數量（基礎量，拆分依此計算）</p>
            <p className="font-semibold tabular-nums text-gray-900">
              {baseQty}
              {stock?.qty?.baseUnit?.name ? ` ${stock.qty.baseUnit.name}` : ''}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {`顯示數量 ${formatInventoryDisplayQty(stock)}`}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex w-full items-center justify-between gap-2">
              <p className="shrink-0 text-sm font-medium text-gray-800">拆出數量</p>
              <Button
                type="button"
                size="sm"
                className="shrink-0"
                disabled={isPending}
                onClick={handleAddRow}
              >
                <PiPlusBold className="size-3.5" aria-hidden />
                新增列
              </Button>
            </div>
            <ul className="m-0 list-none space-y-4 p-0">
              {qtyDrafts.map((draft, index) => (
                <li key={`split-qty-${index}`} className="flex items-start gap-2">
                  <FloatingInput
                    id={`inventory-stock-split-qty-${index}`}
                    label={`拆出 #${index + 1}`}
                    type="number"
                    min={0}
                    step="any"
                    value={draft}
                    readOnly={isPending}
                    onChange={(event) => handleQtyChange(index, event.target.value)}
                    containerClassName="mb-0! min-w-0 flex-1"
                    suppressInlineError
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={cn('mt-2.75', DESTRUCTIVE_GHOST_BUTTON_CLASS)}
                    disabled={isPending || qtyDrafts.length <= 1}
                    aria-label={`移除拆出 #${index + 1}`}
                    onClick={() => handleRemoveRow(index)}
                  >
                    <PiTrashBold className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
            <p
              className={cn(
                'mt-5 text-xs tabular-nums',
                isSumInvalid ? 'text-red-600' : 'text-muted-foreground',
              )}
            >
              {'合計: '}
              <span className="text-base font-semibold text-gray-900">{sumQty}</span>
              {' ／ 來源: '}
              <span className="text-base font-semibold text-gray-900">{baseQty}</span>
              {isSumInvalid
                ? '（必須小於來源數量，不可整筆拆完）'
                : `（剩餘 ${remainingQty}）`}
            </p>
          </div>
        </div>
      )}
    </AppDialog>
  );
};

InventoryStockSplitDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  stock: PropTypes.object,
  isPending: PropTypes.bool,
  onConfirm: PropTypes.func,
};

export default InventoryStockSplitDialog;
